
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Hero from './components/Hero';
import ProgramOverview from './components/ProgramOverview';
import Services from './components/Services';
import About from './components/About';
import StateAgencies from './components/StateAgencies';
import Gallery from './components/Gallery';
import Contact from './components/Contact';

const HomePage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white py-3 px-4">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center text-sm gap-4">
          <div className="flex items-center gap-6 flex-wrap">
            <a href="tel:323-727-5033" className="flex items-center gap-2 hover:text-teal-400 transition-colors cursor-pointer">
              <i className="ri-phone-line"></i>
              <span className="font-medium">323-727-5033</span>
            </a>
            <div className="flex items-center gap-2 text-gray-300">
              <i className="ri-map-pin-line"></i>
              <span>204 S Atlantic Blvd. Los Angeles, CA 90022</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="#" className="w-8 h-8 flex items-center justify-center hover:text-teal-400 transition-colors cursor-pointer" aria-label="Facebook">
              <i className="ri-facebook-fill text-lg"></i>
            </a>
            <a href="#" className="w-8 h-8 flex items-center justify-center hover:text-teal-400 transition-colors cursor-pointer" aria-label="Twitter">
              <i className="ri-twitter-fill text-lg"></i>
            </a>
            <a href="#" className="w-8 h-8 flex items-center justify-center hover:text-teal-400 transition-colors cursor-pointer" aria-label="YouTube">
              <i className="ri-youtube-fill text-lg"></i>
            </a>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-white shadow-sm'}`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex-shrink-0">
              <img 
                src="/images/logo-01.webp" 
                alt="Choix Vocational Services" 
                className="h-16 w-auto"
              />
            </Link>
            
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-8">
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-teal-600 transition-colors font-medium whitespace-nowrap cursor-pointer">About</button>
              <button onClick={() => scrollToSection('program')} className="text-gray-700 hover:text-teal-600 transition-colors font-medium whitespace-nowrap cursor-pointer">Programs</button>
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-teal-600 transition-colors font-medium whitespace-nowrap cursor-pointer">Services</button>
              <button onClick={() => scrollToSection('gallery')} className="text-gray-700 hover:text-teal-600 transition-colors font-medium whitespace-nowrap cursor-pointer">Gallery</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-teal-600 transition-colors font-medium whitespace-nowrap cursor-pointer">Contact</button>
              <Link 
                to="/"
                className="bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-all font-semibold whitespace-nowrap shadow-md hover:shadow-lg"
              >
                Home
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
              <div className="flex flex-col gap-4">
                <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-teal-600 transition-colors font-medium cursor-pointer text-left">About</button>
                <button onClick={() => scrollToSection('program')} className="text-gray-700 hover:text-teal-600 transition-colors font-medium cursor-pointer text-left">Programs</button>
                <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-teal-600 transition-colors font-medium cursor-pointer text-left">Services</button>
                <button onClick={() => scrollToSection('gallery')} className="text-gray-700 hover:text-teal-600 transition-colors font-medium cursor-pointer text-left">Gallery</button>
                <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-teal-600 transition-colors font-medium cursor-pointer text-left">Contact</button>
                <Link 
                  to="/"
                  className="bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-all font-semibold text-center"
                >
                  Home
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <Hero />

      {/* About Section */}
      <div id="about">
        <About />
      </div>

      {/* Program Overview */}
      <div id="program">
        <ProgramOverview />
      </div>

      {/* Services Section */}
      <div id="services">
        <Services />
      </div>

      {/* State Agencies */}
      <StateAgencies />

      {/* Gallery Section */}
      <div id="gallery">
        <Gallery />
      </div>

      {/* Contact Section */}
      <div id="contact">
        <Contact />
      </div>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            {/* About Column */}
            <div className="md:col-span-2">
              <img 
                src="/images/logo-01.webp" 
                alt="Choix Vocational Services" 
                className="h-16 w-auto mb-6 brightness-0 invert"
              />
              <p className="text-gray-300 leading-relaxed mb-6 text-lg">
                Empowering individuals with disabilities through personalized support, training, and comprehensive vocational services since 2012.
              </p>
              <div className="flex items-center gap-3">
                <a href="#" className="w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-teal-600 rounded-lg transition-all cursor-pointer" aria-label="Facebook">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="#" className="w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-teal-600 rounded-lg transition-all cursor-pointer" aria-label="Twitter">
                  <i className="ri-twitter-fill text-xl"></i>
                </a>
                <a href="#" className="w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-teal-600 rounded-lg transition-all cursor-pointer" aria-label="YouTube">
                  <i className="ri-youtube-fill text-xl"></i>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-xl font-bold mb-6">Quick Links</h3>
              <div className="flex flex-col gap-3">
                <button onClick={() => scrollToSection('about')} className="text-gray-300 hover:text-teal-400 transition-colors cursor-pointer text-left">About Us</button>
                <button onClick={() => scrollToSection('program')} className="text-gray-300 hover:text-teal-400 transition-colors cursor-pointer text-left">Programs</button>
                <button onClick={() => scrollToSection('services')} className="text-gray-300 hover:text-teal-400 transition-colors cursor-pointer text-left">Services</button>
                <button onClick={() => scrollToSection('gallery')} className="text-gray-300 hover:text-teal-400 transition-colors cursor-pointer text-left">Gallery</button>
                <button onClick={() => scrollToSection('contact')} className="text-gray-300 hover:text-teal-400 transition-colors cursor-pointer text-left">Contact</button>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-xl font-bold mb-6">Contact Info</h3>
              <div className="flex flex-col gap-4 text-gray-300">
                <div className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-teal-400 mt-1 text-xl"></i>
                  <span className="leading-relaxed">204 S Atlantic Blvd.<br/>Los Angeles, CA 90022</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-phone-line text-teal-400 text-xl"></i>
                  <a href="tel:323-727-5033" className="hover:text-teal-400 transition-colors cursor-pointer font-medium">323-727-5033</a>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-700 pt-8 flex flex-wrap justify-between items-center gap-4 text-sm text-gray-400">
            <div>
              © 2012-2025 Choix Vocational Services. All rights reserved.
            </div>
            <div>
              <a href="https://readdy.ai/?ref=logo" className="hover:text-teal-400 transition-colors cursor-pointer" target="_blank" rel="noopener noreferrer">Website Builder</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
